import React, { useState, useEffect } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import {
  Card,
  Table,
  Button,
  Space,
  DatePicker,
  message,
  Spin,
  Select,
  Segmented,
  Input,
  Switch,
} from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const { RangePicker } = DatePicker;
const { Option } = Select;

const PropertyWisePLReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [downloading, setDownloading] = useState(false);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);
  const [type, setType] = useState(undefined);
  const [codeNo, setCodeNo] = useState("");
  const [selectedGroup, setSelectedGroup] = useState(undefined);
  const [groupOptions, setGroupOptions] = useState([]);
  const [isSummary, setIsSummary] = useState(false);
  const [grandTotal, setGrandTotal] = useState(null);

  const beginRequest = useLatestRequest();

  const fetchData = async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    setGrandTotal(null);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isSummary", isSummary ? "true" : "false");
      if (dateRange && dateRange[0])
        params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
      if (dateRange && dateRange[1])
        params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }
      if (codeNo?.trim()) params.set("codeNo", codeNo.trim());
      if (selectedGroup) params.set("group", selectedGroup);
      if (type) params.set("type", type);
      const q = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getPropertyWisePL${q}`,
        {
          method: "GET",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        },
      );
      const j = await res.json();
      if (!isCurrent()) return;
      if (j.success) {
        setGrandTotal(j.grandTotal != null ? j.grandTotal : null);
        if (isSummary) {
          setRows(j.data || []);
        } else {
          const data = (j.data || []).map((r, i) => ({
            __rowKey: r.invId || `r_${i}`,
            ...r,
          }));
          setRows(data);
          const groups = Array.from(
            new Set((j.data || []).map((r) => r.groupName)),
          ).filter(Boolean);
          groups.sort((a, b) => String(a).localeCompare(String(b)));
          setGroupOptions(groups.map((g) => ({ label: g, value: g })));
        }
      } else {
        message.error("Failed to load report");
        setGrandTotal(null);
      }
    } catch (err) {
      if (!isCurrent()) return;
      console.error(err);
      message.error("Error fetching report");
      setGrandTotal(null);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, selectedSuffix, type, codeNo, selectedGroup, isSummary, financialYear]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("excel", "true");
      params.set("isSummary", isSummary ? "true" : "false");
      if (dateRange && dateRange[0])
        params.set("startDate", dateRange[0].format("DD-MM-YYYY"));
      if (dateRange && dateRange[1])
        params.set("endDate", dateRange[1].format("DD-MM-YYYY"));
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0) {
        params.set("suffix", selectedSuffix.join(","));
      }
      if (codeNo?.trim()) params.set("codeNo", codeNo.trim());
      if (selectedGroup) params.set("group", selectedGroup);
      if (type) params.set("type", type);
      const q = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(
        `${apiBaseUrl}/inventoryManagement/getPropertyWisePL${q}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            Accept:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, */*",
          },
        },
      );

      if (!res.ok) {
        const txt = await res.text().catch(() => "Failed to download");
        message.error(txt || "Failed to download");
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = isSummary
        ? `Property_Wise_PL_Summary_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`
        : `Property_Wise_PL_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      message.error("Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const fmt = (v) =>
    v != null ? new Intl.NumberFormat("en-IN").format(v) : "-";

  const columns = [
    {
      title: "Property Code",
      dataIndex: "propertyCode",
      key: "propertyCode",
      width: 160,
      align: "center",
    },
    {
      title: "Suffix",
      dataIndex: "suffix",
      key: "suffix",
      width: 90,
      align: "center",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 220,
      align: "left",
    },
    {
      title: "Sales Amount",
      dataIndex: "salesAmount",
      key: "salesAmount",
      width: 140,
      align: "right",
      render: fmt,
    },
    {
      title: "Purchase Amount",
      dataIndex: "purchaseAmount",
      key: "purchaseAmount",
      width: 140,
      align: "right",
      render: fmt,
    },
    {
      title: "Profit / Loss",
      dataIndex: "profitLoss",
      key: "profitLoss",
      width: 140,
      align: "right",
      render: fmt,
    },
    {
      title: "Profit %",
      dataIndex: "profitLossPct",
      key: "profitLossPct",
      width: 120,
      align: "center",
      render: (v) => `${Number(v || 0).toFixed(2)}%`,
    },
  ];

  const components = {
    header: {
      cell: (props) => (
        <th {...props} style={{ ...props.style, backgroundColor: "#daeffe" }} />
      ),
    },
  };

  const summaryColumns = [
    {
      title: "Property Code",
      dataIndex: "propertyCode",
      key: "propertyCode",
      width: 160,
      align: "center",
    },
    {
      title: "Sales Amount",
      dataIndex: "salesAmount",
      key: "salesAmount",
      width: 140,
      align: "center",
      render: fmt,
    },
    {
      title: "Purchase Amount",
      dataIndex: "purchaseAmount",
      key: "purchaseAmount",
      width: 140,
      align: "center",
      render: fmt,
    },
    {
      title: "Profit / Loss",
      dataIndex: "profitLoss",
      key: "profitLoss",
      width: 140,
      align: "center",
      render: fmt,
    },
  ];

  return (
    <div
      className="parent-padding-container"
      style={{ backgroundColor: "white", borderRadius: 12, padding: 20 }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Space size="small" style={{ flexShrink: 0 }}>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              <span style={{ fontWeight: 600, fontSize: 16 }}>Property Wise P&L</span>
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: "12px 0",
                flexShrink: 0,
              }}
            >
              <Space wrap size="middle">
                <Switch
                  checkedChildren="Summary"
                  unCheckedChildren="Detailed"
                  checked={isSummary}
                  onChange={setIsSummary}
                />
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Segmented
                  value={type}
                  onChange={setType}
                  options={[
                    { label: "All", value: undefined },
                    { label: "Owned", value: "OWNED" },
                    { label: "Trading", value: "TRADING" },
                  ]}
                />
                <Select
                  mode="multiple"
                  placeholder="Filter by Suffix"
                  allowClear
                  value={
                    Array.isArray(selectedSuffix) && selectedSuffix.length
                      ? selectedSuffix
                      : undefined
                  }
                  onChange={(val) => setSelectedSuffix(val ?? [])}
                  options={DEFAULT_SUFFIX_OPTIONS}
                  style={{ minWidth: 140 }}
                  dropdownRender={selectAllDropdownRender(
                    DEFAULT_SUFFIX_OPTIONS,
                    Array.isArray(selectedSuffix) ? selectedSuffix : [],
                    setSelectedSuffix,
                  )}
                />
                <Input
                  placeholder="Property Code"
                  value={codeNo}
                  onChange={(e) => setCodeNo(e.target.value)}
                  style={{ width: 160 }}
                />
              </Space>
              <Space wrap size="middle">
                <Select
                  placeholder="Filter by Group"
                  allowClear
                  showSearch
                  value={selectedGroup}
                  onChange={setSelectedGroup}
                  options={groupOptions}
                  filterOption={(input, option) => {
                    const label = String(option?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                  style={{ width: 200 }}
                />
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="DD-MM-YYYY"
                  style={{ width: 220 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  onClick={handleDownload}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : rows.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>
            No data found
          </div>
        ) : isSummary ? (
          <Table
            columns={summaryColumns}
            dataSource={rows}
            rowKey={(r, i) => r.propertyCode ?? `summary-${i}`}
            pagination={false}
            scroll={{ x: "max-content" }}
            components={components}
            size="small"
            summary={() =>
              grandTotal ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      <strong>{fmt(grandTotal.salesAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="center">
                      <strong>{fmt(grandTotal.purchaseAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="center">
                      <strong>{fmt(grandTotal.profitLoss)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        ) : (
          <Table
            columns={columns}
            dataSource={rows}
            rowKey={(r) => `${r.invId}-${r.suffix}`}
            pagination={false}
            scroll={{ y: 500 }}
            components={components}
            sticky
            summary={() =>
              grandTotal ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <strong>{fmt(grandTotal.salesAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <strong>{fmt(grandTotal.purchaseAmount)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <strong>{fmt(grandTotal.profitLoss)}</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="center" />
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null
            }
          />
        )}
      </Card>
    </div>
  );
};

export default PropertyWisePLReport;
